import type { CreateRecordType } from 'components/create-dialog/recordTypes';
import type { GlobalSearchEntityType } from 'types/globalSearch';
import type { ListKey } from 'types/listViews';
import type { PinKind } from 'utils/pins';

/**
 * The new design's record families, each drawn in its own colour wherever a record, a list or a menu section is
 * marked: `tile` is the fill behind a glyph, `ink` the glyph on it and the record-type word, `mark` a saved view's
 * solid square. Compliance and Reports draw the neutral family tokens. The utilities exist only in the new design.
 */
export const FAMILY = {
    cases: { tile: 'bg-family-cases-subtle', ink: 'text-family-cases-text', mark: 'bg-family-cases-text' },
    contacts: { tile: 'bg-family-contacts-subtle', ink: 'text-family-contacts-text', mark: 'bg-family-contacts-text' },
    bd: { tile: 'bg-family-bd-subtle', ink: 'text-family-bd-text', mark: 'bg-family-bd-text' },
    people: { tile: 'bg-family-people-subtle', ink: 'text-family-people-text', mark: 'bg-family-people-text' },
    finance: { tile: 'bg-family-finance-subtle', ink: 'text-family-finance-text', mark: 'bg-family-finance-text' },
    compliance: {
        tile: 'bg-family-compliance-subtle',
        ink: 'text-family-compliance-text',
        mark: 'bg-family-compliance-text',
    },
    reports: { tile: 'bg-family-reports-subtle', ink: 'text-family-reports-text', mark: 'bg-family-reports-text' },
} as const;

export type FamilyId = keyof typeof FAMILY;

/** The tile of whatever belongs to no family. */
export const NEUTRAL_TILE_CLASS = 'bg-neutral-tint text-foreground-secondary';

/** A family's tile with its glyph ink, or the neutral tile. */
export const familyTileClass = (family: FamilyId | undefined): string =>
    family ? `${FAMILY[family].tile} ${FAMILY[family].ink}` : NEUTRAL_TILE_CLASS;

/** The family of each record that pins, by its pin kind. */
export const RECORD_KIND_FAMILY: Record<PinKind, FamilyId | undefined> = {
    matter: 'cases',
    projects: 'cases',
    delegations: 'cases',
    correspondence: 'cases',
    contact: 'contacts',
    group: 'contacts',
    contract: 'contacts',
    effort: 'bd',
    'experience-records': 'bd',
    debriefing: 'bd',
    employee: 'people',
    team: 'people',
    office: 'people',
    'practice-area': 'people',
    trainings: 'people',
    invoice: 'finance',
    expense: 'finance',
    'bank-transfers': 'finance',
    task: undefined,
    timecards: undefined,
    'job-role': undefined,
    'job-position': undefined,
};

/** The family of each record the create dialog makes. */
export const CREATE_RECORD_FAMILY: Record<CreateRecordType, FamilyId | undefined> = {
    case: 'cases',
    project: 'cases',
    correspondence: 'cases',
    contact: 'contacts',
    group: 'contacts',
    contract: 'contacts',
    'marketing-profile': 'contacts',
    'bd-effort': 'bd',
    'experience-record': 'bd',
    debriefing: 'bd',
    employee: 'people',
    team: 'people',
    office: 'people',
    'practice-area': 'people',
    training: 'people',
    expense: 'finance',
    'client-asset': 'finance',
    timecard: undefined,
    task: undefined,
    'job-role': undefined,
    'job-position': undefined,
};

/** The family of each list that holds saved views. */
export const LIST_FAMILY: Record<ListKey, FamilyId | undefined> = {
    matters: 'cases',
    projects: 'cases',
    correspondence: 'cases',
    contacts: 'contacts',
    groups: 'contacts',
    contracts: 'contacts',
    'marketing-profiles': 'contacts',
    efforts: 'bd',
    'rankings-referees': 'bd',
    'experience-records': 'bd',
    'law-firms': 'bd',
    debriefings: 'bd',
    employees: 'people',
    teams: 'people',
    offices: 'people',
    'practice-areas': 'people',
    trainings: 'people',
    invoices: 'finance',
    'billable-clients': 'finance',
    pricelists: 'finance',
    expenses: 'finance',
    prepayments: 'finance',
    'bank-transfers': 'finance',
    compliance: 'compliance',
    tasks: undefined,
    'my-portfolio': undefined,
    timecards: undefined,
};

/** The family of each record search returns, by its search family. */
export const ENTITY_TYPE_FAMILY = {
    MATTER: 'cases',
    PROJECT: 'cases',
    DELEGATION: 'cases',
    CORRESPONDENCE: 'cases',
    PARTY: 'contacts',
    PARTY_GROUP: 'contacts',
    CONTRACT: 'contacts',
    EFFORT: 'bd',
    EXPERIENCE_RECORD: 'bd',
    DEBRIEFING: 'bd',
    EMPLOYEE: 'people',
    TEAM: 'people',
    OFFICE: 'people',
    PRACTICE_AREA: 'people',
    INVOICE: 'finance',
    PREPAYMENT: 'finance',
    EXPENSE: 'finance',
    BANK_TRANSACTION: 'finance',
    PRICELIST: 'finance',
    TIMECARD: undefined,
    JOB_ROLE: undefined,
    JOB_POSITION: undefined,
} as const satisfies Record<GlobalSearchEntityType, FamilyId | undefined>;
